import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { SquareSplitHorizontal, Check, X } from "@phosphor-icons/react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { dropdownContentCollisionClassName, floatingCollisionPadding, floatingLayerClassName } from "@/components/ui/overlays/overlayPositioning";
import type { TableColumnVisibilityProps } from "./DataTable.types";

export function TableColumnVisibility({
  columns,
  visibleColumnIds,
  onVisibleColumnIdsChange,
  label = "Columns",
  resetLabel = "Reset",
  onReset,
  showAllLabel = "Show all",
  onShowAll,
  className
}: TableColumnVisibilityProps) {
  const [open, setOpen] = useState(false);

  const toggle = (id: string) => {
    const next = visibleColumnIds.includes(id)
      ? visibleColumnIds.filter((columnId) => columnId !== id)
      : [...visibleColumnIds, id];
    onVisibleColumnIdsChange(next);
  };

  const showAll = () => {
    if (onShowAll) onShowAll();
    else onVisibleColumnIdsChange(columns.map((column) => column.id));
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="secondary"
          size="sm"
          iconOnly
          aria-label={typeof label === "string" ? label : "Columns"}
          className={cn("data-[state=open]:bfrs-border-border-strong data-[state=open]:bfrs-bg-surface-muted", className)}
        >
          <SquareSplitHorizontal size={16} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <BfrsPortalScope>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            collisionPadding={floatingCollisionPadding}
            className={cn(floatingLayerClassName, "bfrs-flex bfrs-w-56 bfrs-flex-col bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-shadow-elevated bfrs-animate-content-in", dropdownContentCollisionClassName)}
          >
            <div className="bfrs-flex bfrs-items-center bfrs-justify-between bfrs-border-b bfrs-border-border bfrs-px-3 bfrs-py-2">
              <span className="bfrs-text-[13px] bfrs-font-bold bfrs-text-text-primary">{label}</span>
              <button
                type="button"
                aria-label="Close"
                className="bfrs-flex bfrs-size-5 bfrs-items-center bfrs-justify-center bfrs-rounded-md bfrs-text-text-muted bfrs-outline-none bfrs-transition hover:bfrs-bg-surface-muted hover:bfrs-text-text-primary focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus"
                onClick={() => setOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="bfrs-max-h-72 bfrs-overflow-auto bfrs-p-1">
              {columns.map((column) => {
                const checked = visibleColumnIds.includes(column.id);
                return (
                  <DropdownMenu.CheckboxItem
                    key={column.id}
                    checked={checked}
                    disabled={column.disabled}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={() => toggle(column.id)}
                    className={cn(
                      "bfrs-flex bfrs-cursor-pointer bfrs-select-none bfrs-items-center bfrs-gap-2 bfrs-rounded-md bfrs-px-3 bfrs-py-1.5 bfrs-text-xs bfrs-text-text-primary bfrs-outline-none data-[disabled]:bfrs-cursor-default data-[disabled]:bfrs-pointer-events-none data-[highlighted]:bfrs-bg-surface-muted data-[disabled]:bfrs-text-text-muted"
                    )}
                  >
                    <span
                      className={cn(
                        "bfrs-flex bfrs-size-4 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded bfrs-border bfrs-transition",
                        checked ? "bfrs-border-primary bfrs-bg-primary bfrs-text-primary-contrast" : "bfrs-border-border bfrs-bg-surface-card",
                        column.disabled && "bfrs-opacity-50"
                      )}
                    >
                      {checked && <Check size={11} weight="bold" />}
                    </span>
                    <span className="bfrs-min-w-0 bfrs-flex-1 bfrs-truncate">{column.label}</span>
                    {!checked && <span className="bfrs-text-[10px] bfrs-text-text-muted">hidden</span>}
                  </DropdownMenu.CheckboxItem>
                );
              })}
            </div>
            <div className="bfrs-flex bfrs-items-center bfrs-gap-2 bfrs-border-t bfrs-border-border bfrs-px-3 bfrs-py-2">
              <Button variant="secondary" size="sm" className="bfrs-flex-1" onClick={showAll}>
                {showAllLabel}
              </Button>
              {onReset && (
                <Button variant="primary" size="sm" className="bfrs-flex-1" onClick={onReset}>
                  {resetLabel}
                </Button>
              )}
            </div>
          </DropdownMenu.Content>
        </BfrsPortalScope>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
